// src/services/specialAccessService.js
import firebase, { auth, db } from '../firebaseConfig.js';


console.log("Special access service initializing...");

// Constants for local storage 
const SPECIAL_ACCESS_KEY = 'deckops_special_access';
const SPECIAL_ACCESS_EXPIRY_KEY = 'deckops_special_access_expiry';
const SPECIAL_ACCESS_USER_ID_KEY = 'deckops_special_access_user_id';
const SPECIAL_ACCESS_USERNAME_KEY = 'deckops_special_access_username';
const SPECIAL_ACCESS_CODE_KEY = 'deckops_special_access_code';
const SPECIAL_ACCESS_SESSION_ID_KEY = 'deckops_special_access_session_id';

/**
 * Validates a special access code 
 * @param {string} accessCode - The code to validate
 * @returns {Promise<Object>} Result with success flag and message
 */
export async function validateAccessCode(accessCode) {
    if (!accessCode || accessCode.trim().length < 6) {
        return { success: false, message: "Invalid access code format" };
    }
    
    try {
        // Normalize the code
        const normalizedCode = accessCode.trim().toUpperCase();
        
        // Hardcoded access codes with expiration dates
        const validCodes = {
            // Format: CODE: { expiresAt: timestamp, durationHours: 24 }
            "ACCESS2025APRIL": { expiresAt: new Date(2025, 4, 30), durationHours: 24 }, 
            "DEVACCESS999": { expiresAt: new Date(2026, 0, 1), durationHours: 720 } // 30 days
        };
        
        let isValid = false;
        let accessDuration = 24; // Default 24 hours
        let expiryDate = null;

        // Check hardcoded codes first
        if (validCodes[normalizedCode]) {
            if (validCodes[normalizedCode].expiresAt < new Date()) {
                return { success: false, message: "This access code has expired" };
            }
            isValid = true;
            accessDuration = validCodes[normalizedCode].durationHours || 24;
            expiryDate = validCodes[normalizedCode].expiresAt;
        } else {
            // If not found in hardcoded list, check Firestore
            const codeDoc = await db.collection("accessCodes")
                .doc(normalizedCode)
                .get();
            
            if (codeDoc.exists) {
                const codeData = codeDoc.data();
                
                // Check if code is still valid
                if (codeData.expiresAt && codeData.expiresAt.toDate() < new Date()) {
                    return { success: false, message: "This access code has expired" };
                }
                
                if (codeData.active === false) {
                    return { success: false, message: "This access code is no longer active" };
                }
                
                if (codeData.maxUses && codeData.useCount >= codeData.maxUses) {
                    return { success: false, message: "This access code has reached its usage limit" };
                }
                
                // Code is valid in Firestore
                isValid = true;
                accessDuration = codeData.durationHours || 24;
                expiryDate = codeData.expiresAt?.toDate() || null;
                
                // Update usage statistics
                try {
                    await codeDoc.ref.update({
                        useCount: firebase.firestore.FieldValue.increment(1),
                        lastUsed: firebase.firestore.FieldValue.serverTimestamp(),
                        lastUsedBy: auth.currentUser?.uid || "guest",
                    });
                } catch (updateError) {
                    console.warn("Could not update access code usage:", updateError);
                    // Non-critical error, continue with access grant
                }
            }
        }
        
        if (!isValid) {
            return { success: false, message: "Invalid access code" };
        }
        
        // Code is valid - set up special access in localStorage
        const expiryTime = Date.now() + (accessDuration * 60 * 60 * 1000);
        
        // Generate a unique session ID
        const sessionId = 'guestplus_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
        
        // Store access info in localStorage
        localStorage.setItem(SPECIAL_ACCESS_KEY, 'true');
        localStorage.setItem(SPECIAL_ACCESS_EXPIRY_KEY, expiryTime.toString());
        localStorage.setItem(SPECIAL_ACCESS_CODE_KEY, normalizedCode);
        localStorage.setItem(SPECIAL_ACCESS_SESSION_ID_KEY, sessionId);
        
        // Try to log access usage for analytics
        try {
            await db.collection('accessCodeLogs').add({
                code: normalizedCode,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                deviceInfo: navigator.userAgent,
                userId: auth.currentUser?.uid || null,
                sessionId: sessionId,
                expiryTime: new Date(expiryTime)
            });
        } catch (logError) {
            console.warn("Failed to log access code usage:", logError);
            // Non-critical error, continue with access grant
        }
        
        // Return success with expiry information
        return { 
            success: true, 
            message: `Access granted for ${accessDuration} hours!`,
            expiryTime: expiryTime,
            expiryDate: expiryDate,
            sessionId: sessionId
        };
    } catch (error) {
        console.error("Error validating access code:", error);
        return { success: false, message: "Error checking code. Please try again." };
    }
}

/**
 * Checks if the user currently has special access
 * @returns {boolean} Whether the user has valid special access
 */
export function hasSpecialAccess() {
    try {
        const isSpecialAccess = localStorage.getItem(SPECIAL_ACCESS_KEY) === 'true';
        
        if (isSpecialAccess) {
            // Check for expiration
            const expiryTimeStr = localStorage.getItem(SPECIAL_ACCESS_EXPIRY_KEY);
            if (!expiryTimeStr) {
                console.log("Special access key exists but no expiry time found. Clearing.");
                clearSpecialAccess();
                return false;
            }
            
            const expiryTime = parseInt(expiryTimeStr, 10);
            const now = Date.now();
            
            if (expiryTime <= now) {
                console.log("Special access has expired. Clearing.");
                clearSpecialAccess();
                return false;
            }
            
            // Log the active special access
            const hoursRemaining = getSpecialAccessHoursRemaining();
            console.log(`Special access is active with ${hoursRemaining} hours remaining.`);
            return true;
        }
        
        return false;
    } catch (error) {
        console.error("Error checking special access:", error);
        return false;
    }
}

/**
 * Clear all special access data and notify the system
 */
export function clearSpecialAccess() {
    localStorage.removeItem(SPECIAL_ACCESS_KEY);
    localStorage.removeItem(SPECIAL_ACCESS_EXPIRY_KEY);
    localStorage.removeItem(SPECIAL_ACCESS_USER_ID_KEY);
    localStorage.removeItem(SPECIAL_ACCESS_USERNAME_KEY);
    localStorage.removeItem(SPECIAL_ACCESS_CODE_KEY);
    
    // Clear session ID but first get it for logging
    const sessionId = localStorage.getItem(SPECIAL_ACCESS_SESSION_ID_KEY);
    localStorage.removeItem(SPECIAL_ACCESS_SESSION_ID_KEY);
    
    // Log session end if we have a session ID
    if (sessionId) {
        try {
            db.collection('accessCodeLogs').where('sessionId', '==', sessionId).get()
                .then(snapshot => {
                    if (!snapshot.empty) {
                        snapshot.forEach(doc => {
                            doc.ref.update({
                                endedAt: firebase.firestore.FieldValue.serverTimestamp(),
                                endReason: 'manual_clear'
                            });
                        });
                    }
                })
                .catch(err => console.warn("Failed to log session end:", err));
        } catch (e) {
            console.warn("Error logging session end:", e);
        }
    }
    
    // Dispatch event to notify the system
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('special-access-cleared'));
    }
}

/**
 * Gets the expiry time of the current special access
 * @returns {Date|null} Expiry date or null if no access
 */
export function getSpecialAccessExpiry() {
    const expiryTimeStr = localStorage.getItem(SPECIAL_ACCESS_EXPIRY_KEY);
    if (!expiryTimeStr) return null;
    
    const expiryTime = parseInt(expiryTimeStr, 10);
    return new Date(expiryTime);
}

/**
 * Gets the remaining time of special access in hours
 * @returns {number} Hours remaining or 0 if no access
 */
export function getSpecialAccessHoursRemaining() {
    const expiry = getSpecialAccessExpiry();
    if (!expiry) return 0;
    
    const now = new Date();
    const diffMs = expiry - now;
    if (diffMs <= 0) return 0;
    
    return Math.round(diffMs / (1000 * 60 * 60));
}

/**
 * Get or create a consistent temporary user ID for Guest+ users
 * @returns {string} Temporary user ID for Guest+ users
 */
export function getSpecialAccessUserId() {
    let tempUserId = localStorage.getItem(SPECIAL_ACCESS_USER_ID_KEY);
    if (!tempUserId) {
        // Creates a NEW ID every time localStorage is empty for this key
        tempUserId = 'guestplus_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
        localStorage.setItem(SPECIAL_ACCESS_USER_ID_KEY, tempUserId);
    }
    return tempUserId;
}

/**
 * Get the display name for a Guest+ user
 * @returns {string} Display name with hours remaining
 */
export function getSpecialAccessDisplayName() {
    // Check if we already have a username stored
    let username = localStorage.getItem(SPECIAL_ACCESS_USERNAME_KEY);
    
    // If not, create a default one with hours remaining
    if (!username) {
        const hoursRemaining = getSpecialAccessHoursRemaining();
        username = `Guest+ (${hoursRemaining}h)`;
        localStorage.setItem(SPECIAL_ACCESS_USERNAME_KEY, username);
    }
    
    return username;
}

/**
 * Update the display name for a Guest+ user (call periodically to update hours)
 */
export function updateSpecialAccessDisplayName() {
    const hoursRemaining = getSpecialAccessHoursRemaining();
    const username = `Guest+ (${hoursRemaining}h)`;
    localStorage.setItem(SPECIAL_ACCESS_USERNAME_KEY, username);
    return username;
}

/**
 * Gets the special access session ID
 * @returns {string|null} Session ID or null if not set
 */
export function getSpecialAccessSessionId() {
    return localStorage.getItem(SPECIAL_ACCESS_SESSION_ID_KEY);
}

/**
 * Register listeners for page unload to track session end
 */
export function registerSessionEndListeners() {
    if (typeof window === 'undefined') return;
    
    // Handle page unload/close
    window.addEventListener('beforeunload', () => {
        const sessionId = getSpecialAccessSessionId();
        if (sessionId) {
            try {
                // Use synchronous approaches since this is a beforeunload handler
                navigator.sendBeacon('/api/log-session-end', JSON.stringify({
                    sessionId,
                    endReason: 'page_close'
                }));
            } catch (e) {
                // Beacon failed or not supported
                console.warn("Failed to send page close beacon");
            }
        }
    });
    
    // Handle visibility change (tab hide/show)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // Tab became visible - check if access still valid
            if (!hasSpecialAccess()) {
                // If no longer valid, dispatch event
                window.dispatchEvent(new CustomEvent('special-access-expired'));
            }
        }
    });
}

/**
 * Checks if the user has admin privileges via special access
 * @returns {boolean} Whether the user has admin access
 */
export function hasSpecialAdminAccess() {
    // Get the access code from localStorage
    const accessCode = localStorage.getItem(SPECIAL_ACCESS_CODE_KEY);
    
    // Check if it's an admin access code (you can define specific admin codes)
    return accessCode === 'DEVACCESS999' || accessCode === 'ADMIN2025APRIL';
}

// Register session listeners when this module loads
if (typeof window !== 'undefined') {
    registerSessionEndListeners();
}

export default {
    validateAccessCode,
    hasSpecialAccess,
    clearSpecialAccess,
    getSpecialAccessExpiry,
    getSpecialAccessHoursRemaining,
    getSpecialAccessUserId,
    getSpecialAccessDisplayName,
    updateSpecialAccessDisplayName,
    getSpecialAccessSessionId,
    hasSpecialAdminAccess
};